import { PopulatedDesignerApplication, HomeOwnerUser } from "../__generated__/types";

/**
 * Comprehensive search function for designer applications
 * Searches across all available data fields
 */
export const searchDesignerApplications = (
  applications: PopulatedDesignerApplication[],
  searchTerm: string
): PopulatedDesignerApplication[] => {
  if (!searchTerm.trim()) return applications;

  const normalizedSearch = searchTerm.toLowerCase().trim();

  return applications.filter((app) => {
    const user = app.designerId;
    if (!user) return false;

    // Basic user information
    const basicFields = [
      user.firstName,
      user.lastName,
      user.email,
      user.businessName,
      user.phoneNumber,
      user.aliasId,
      user.subscription,
      user.title,
      user.aboutMe,
    ].filter(Boolean);

    // Additional business fields that might be available
    const businessFields = [
      (user as any).businessCity,
      (user as any).businessState,
      (user as any).businessAddress,
      (user as any).businessZipCode,
      (user as any).businessWebsite,
      (user as any).workLocation,
      (user as any).communicationPreference,
    ].filter(Boolean);

    // Application status and membership
    const applicationFields = [
      app.status,
      app.membershipTier,
    ].filter(Boolean);

    // Experience data
    const experienceFields = app.experience?.flatMap(exp => 
      exp ? [
        exp.title,
        exp.company,
        exp.employmentType,
        exp.firmSize,
      ] : []
    ) || [];

    // Education data
    const educationFields = app.education || [];

    // References data
    const referenceFields = app.references?.flatMap(ref => 
      ref ? [
        ref.firstName,
        ref.lastName,
        ref.businessName,
        ref.title,
        ref.type,
        ref.email,
        ref.phoneNumber,
        ref.ProjectType,
      ] : []
    ) || [];

    // Business goals data
    const businessGoalsFields = [
      app.businessGoals?.challenges,
      app.businessGoals?.expectations,
      app.businessGoals?.overview?.totalRevenueLastYear,
      app.businessGoals?.overview?.totalRevenueCurrentYear,
      app.businessGoals?.projectCapacity?.largeScale,
      app.businessGoals?.projectCapacity?.smallScale,
    ].filter(Boolean);

    // Single payment items
    const paymentFields = app.singlePaymentItems?.map(item => item?.toString()).filter(Boolean) || [];

    // Combine all searchable fields
    const allFields = [
      ...basicFields,
      ...businessFields,
      ...applicationFields,
      ...experienceFields,
      ...educationFields,
      ...referenceFields,
      ...businessGoalsFields,
      ...paymentFields,
    ];

    // Check if any field contains the search term
    return allFields.some(field => 
      field?.toString().toLowerCase().includes(normalizedSearch)
    );
  });
};

/**
 * Comprehensive search function for homeowner accounts
 * Searches across all available data fields
 */
export const searchHomeownerAccounts = (
  homeowners: HomeOwnerUser[],
  searchTerm: string
): HomeOwnerUser[] => {
  if (!searchTerm.trim()) return homeowners;

  const normalizedSearch = searchTerm.toLowerCase().trim();

  return homeowners.filter((homeowner) => {
    // Basic homeowner information
    const basicFields = [
      homeowner.firstName,
      homeowner.lastName,
      homeowner.email,
      homeowner.aliasId,
      homeowner.subscription,
      homeowner.wixMemberId,
    ].filter(Boolean);

    // Additional fields that might be available
    const additionalFields = [
      (homeowner as any).paymentStatus,
      (homeowner as any).transactionId,
      (homeowner as any).authorizationCode,
      (homeowner as any).paymentDate,
      (homeowner as any).subscriptionDate,
      (homeowner as any).billingInfo?.firstName,
      (homeowner as any).billingInfo?.lastName,
      (homeowner as any).billingInfo?.address,
      (homeowner as any).billingInfo?.city,
      (homeowner as any).billingInfo?.state,
      (homeowner as any).billingInfo?.zipCode,
      (homeowner as any).billingInfo?.email,
      (homeowner as any).billingInfo?.phoneNumber,
      (homeowner as any).subscriptionDetails?.planType,
      (homeowner as any).subscriptionDetails?.billingCycle,
      (homeowner as any).subscriptionDetails?.amount?.toString(),
      (homeowner as any).subscriptionDetails?.startDate,
      (homeowner as any).subscriptionDetails?.endDate,
    ].filter(Boolean);

    // Combine all searchable fields
    const allFields = [
      ...basicFields,
      ...additionalFields,
    ];

    // Check if any field contains the search term
    return allFields.some(field => 
      field?.toString().toLowerCase().includes(normalizedSearch)
    );
  });
};

/**
 * Get search suggestions based on available data
 */
export const getSearchSuggestions = (
  applications: PopulatedDesignerApplication[],
  homeowners: HomeOwnerUser[]
): string[] => {
  const suggestions = new Set<string>();

  // Add suggestions from designer applications
  applications.forEach(app => {
    const user = app.designerId;
    if (user) {
      if (user.firstName) suggestions.add(user.firstName);
      if (user.lastName) suggestions.add(user.lastName);
      if (user.businessName) suggestions.add(user.businessName);
      if ((user as any).businessCity) suggestions.add((user as any).businessCity);
      if ((user as any).businessState) suggestions.add((user as any).businessState);
      if (app.status) suggestions.add(app.status);
      if (app.membershipTier) suggestions.add(app.membershipTier);
      
      // Add experience companies
      app.experience?.forEach(exp => {
        if (exp?.company) suggestions.add(exp.company);
        if (exp?.title) suggestions.add(exp.title);
      });
      
      // Add reference names
      app.references?.forEach(ref => {
        if (ref?.firstName) suggestions.add(ref.firstName);
        if (ref?.lastName) suggestions.add(ref.lastName);
        if (ref?.businessName) suggestions.add(ref.businessName);
      });
    }
  });

  // Add suggestions from homeowner accounts
  homeowners.forEach(homeowner => {
    if (homeowner.firstName) suggestions.add(homeowner.firstName);
    if (homeowner.lastName) suggestions.add(homeowner.lastName);
    if (homeowner.subscription) suggestions.add(homeowner.subscription);
    if ((homeowner as any).billingInfo?.city) suggestions.add((homeowner as any).billingInfo.city);
    if ((homeowner as any).billingInfo?.state) suggestions.add((homeowner as any).billingInfo.state);
  });

  return Array.from(suggestions).sort();
};
